import { FC, useEffect, useState } from 'react';
import DateInput from '../custom-input/DateInput';
import NumberInput from '../custom-input/NumberInput';
import { logAccountTankMaintenance } from '../../api/accountTankMaintenanceApi';
import { getTankMaintenanceCustomDataKeys } from '../../api/tankMaintenanceCustomDataKeysApi';
import { showToast } from '../../services/toast.service';
import { CustomDataValueItem, TankMaintenanceCustomDataKey } from '../../types/interfaces/accountTankMaintenance.interfaces';
import { PagedResponse } from '../../types/interfaces';
import { toUtcDateOnlyIso } from '../../utils/tankMaintenance.utils';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS } from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import './tankMaintenance.scss';

type LogAccountTankMaintenanceModalProps = {
  tankId: number;
  tankName: string;
  siteName?: string;
  maintenanceTypeId?: number;
  close: () => void;
  onLogged?: () => void;
};

const LogAccountTankMaintenanceModal: FC<LogAccountTankMaintenanceModalProps> = ({
  tankId,
  tankName,
  siteName,
  maintenanceTypeId,
  close,
  onLogged,
}): JSX.Element => {
  const [performedDate, setPerformedDate] = useState<Date>(new Date());
  const [ln2Level, setLn2Level] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [nextDueOverride, setNextDueOverride] = useState<Date | undefined>();
  const [customKeys, setCustomKeys] = useState<TankMaintenanceCustomDataKey[]>([]);
  const [customValues, setCustomValues] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const loadKeys = async () => {
      try {
        const { data } = await getTankMaintenanceCustomDataKeys({ filter: 'Active eq true', limit: 100 });
        const keys = Array.isArray(data) ? data : (data as PagedResponse<TankMaintenanceCustomDataKey>).result;
        const filtered = keys.filter(
          key =>
            key.active &&
            (key.maintenanceTypeId == null || maintenanceTypeId == null || key.maintenanceTypeId === maintenanceTypeId),
        );
        setCustomKeys(filtered);
      } catch {
        setCustomKeys([]);
      }
    };
    loadKeys();
  }, [maintenanceTypeId]);

  const validateCustomValues = (): boolean => {
    for (const key of customKeys) {
      const value = (customValues[key.tankMaintenanceCustomDataKeyId] ?? '').trim();
      if (key.required && !value) {
        showToast.error(toastMessages.TANK_MAINTENANCE_REQUIRED_FIELD(key.keyName));
        return false;
      }
      if (value && key.isNumeric) {
        const numeric = Number(value);
        if (Number.isNaN(numeric)) {
          showToast.error(toastMessages.TANK_MAINTENANCE_NUMERIC_FIELD(key.keyName));
          return false;
        }
        if (key.minValue != null && numeric < key.minValue) {
          showToast.error(toastMessages.TANK_MAINTENANCE_MIN_VALUE(key.keyName, key.minValue));
          return false;
        }
        if (key.maxValue != null && numeric > key.maxValue) {
          showToast.error(toastMessages.TANK_MAINTENANCE_MAX_VALUE(key.keyName, key.maxValue));
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting || !performedDate) {
      return;
    }
    if (!validateCustomValues()) {
      return;
    }

    const ln2 = ln2Level.trim() === '' ? null : Number(ln2Level);
    if (ln2 != null && (Number.isNaN(ln2) || ln2 < 0 || ln2 > 200)) {
      showToast.error(toastMessages.TANK_MAINTENANCE_LN2_INVALID);
      return;
    }

    const customDataValues: CustomDataValueItem[] = customKeys
      .map(key => ({
        tankMaintenanceCustomDataKeyId: key.tankMaintenanceCustomDataKeyId,
        keyName: key.keyName,
        value: (customValues[key.tankMaintenanceCustomDataKeyId] ?? '').trim(),
      }))
      .filter(item => item.value !== '');

    setSubmitting(true);
    try {
      await logAccountTankMaintenance(tankId, {
        maintenanceTypeId: maintenanceTypeId ?? null,
        location: siteName || null,
        lN2Level: ln2,
        performedDate: toUtcDateOnlyIso(performedDate),
        notes: notes.trim() || null,
        nextDueDateOverride: nextDueOverride ? toUtcDateOnlyIso(nextDueOverride) : null,
        customDataValues,
      });
      showToast.success(toastMessages.TANK_MAINTENANCE_LOGGED);
      onLogged?.();
      close();
    } catch {
      showToast.error(toastMessages.TANK_MAINTENANCE_LOG_FAIL);
    } finally {
      setSubmitting(false);
    }
  };

  const maxOverride = new Date(performedDate);
  maxOverride.setFullYear(maxOverride.getFullYear() + 3);

  return (
    <div className="tank-maintenance-modal">
      <form onSubmit={handleSubmit}>
        <h2>{LABEL_CONSTANTS.LOG_TANK_MAINTENANCE}</h2>
        <hr />
        <p>
          {LABEL_CONSTANTS.TANK}: <strong>{tankName}</strong>
        </p>

        <div className="form-row">
          <label>{LABEL_CONSTANTS.PERFORMED_DATE}</label>
          <div className="input-container">
            <DateInput value={performedDate} onChange={setPerformedDate} maxDate={new Date()} />
          </div>
        </div>

        <div className="form-row">
          <label htmlFor="ln2-level">{LABEL_CONSTANTS.LN2_LEVEL}</label>
          <div className="input-container input-container--number">
            <NumberInput
              id="ln2-level"
              value={ln2Level}
              min={0}
              max={200}
              step="0.01"
              placeholder="0-200"
              onChange={e => setLn2Level(e.target.value)}
            />
          </div>
        </div>

        <div className="form-row form-row--top">
          <label htmlFor="maintenance-notes">{LABEL_CONSTANTS.NOTES}</label>
          <div className="input-container">
            <textarea id="maintenance-notes" rows={3} value={notes} onChange={e => setNotes(e.target.value)} maxLength={2000} />
          </div>
        </div>

        <div className="form-row">
          <label>{LABEL_CONSTANTS.NEXT_DUE_DATE_OVERRIDE}</label>
          <div className="input-container">
            <DateInput
              value={nextDueOverride}
              onChange={date => setNextDueOverride(date || undefined)}
              minDate={performedDate}
              maxDate={maxOverride}
              isClearable
            />
          </div>
        </div>

        {customKeys.map(key => (
          <div className="form-row" key={key.tankMaintenanceCustomDataKeyId}>
            <label htmlFor={`custom-key-${key.tankMaintenanceCustomDataKeyId}`}>
              {key.keyName}
              {key.required ? ' *' : ''}
            </label>
            <div className={`input-container${key.isNumeric ? ' input-container--number' : ''}`}>
              {key.isNumeric ? (
                <NumberInput
                  id={`custom-key-${key.tankMaintenanceCustomDataKeyId}`}
                  value={customValues[key.tankMaintenanceCustomDataKeyId] ?? ''}
                  min={key.minValue ?? undefined}
                  max={key.maxValue ?? undefined}
                  step="0.01"
                  required={key.required}
                  onChange={e => setCustomValues(prev => ({ ...prev, [key.tankMaintenanceCustomDataKeyId]: e.target.value }))}
                />
              ) : (
                <input
                  id={`custom-key-${key.tankMaintenanceCustomDataKeyId}`}
                  type="text"
                  value={customValues[key.tankMaintenanceCustomDataKeyId] ?? ''}
                  onChange={e => setCustomValues(prev => ({ ...prev, [key.tankMaintenanceCustomDataKeyId]: e.target.value }))}
                />
              )}
            </div>
          </div>
        ))}

        <div className="flex-right submit-button tank-maintenance-modal-actions">
          <button type="button" className="small green inverted button" onClick={close}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          &emsp;
          <button
            type="submit"
            className={submitting ? 'small green button disabled' : 'small green button'}
            disabled={submitting}>
            {BUTTON_CONSTANTS.SUBMIT}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LogAccountTankMaintenanceModal;
